import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { SortableTableRow, SortableTableRows } from '@/components/SortableTableRows'
import { NameCell, StatusChip } from '@/components/TableCells'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orderTiersByNetwork, type TierDetail } from '@/services/networks/tiersApi'
import { formatNumber } from '@/utils/format'

interface TierPriorityTableProps {
  tiers: TierDetail[]
  /** The network's enabled tiers, highest priority first. */
  enabledIds: string[]
  onChange: (enabledIds: string[]) => void
  loading?: boolean
}

/**
 * The tiers in waterfall order: enabled tiers are dragged into priority order,
 * and switched on or off. Disabled tiers follow under their own heading.
 */
export function TierPriorityTable({ tiers, enabledIds, onChange, loading }: TierPriorityTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const rows = orderTiersByNetwork(tiers, enabledIds)
  const enabledRows = rows.filter((tier) => tier.priority != null)
  const disabledRows = rows.filter((tier) => tier.priority == null)
  const columnCount = 9

  const toggle = (id: string, enabled: boolean) =>
    onChange(enabled ? [...enabledIds, id] : enabledIds.filter((i) => i !== id))

  const cells = (tier: TierDetail & { priority: number | null }) => {
    const enabled = tier.priority != null
    return (
      <>
        <TableCell className="text-(--ad-text-muted)">{tier.priority ?? '—'}</TableCell>
        <TableCell>
          <Link to={`/admin/tiers/${tier.id}`} onClick={(e) => e.stopPropagation()}>
            <NameCell name={tier.name} id={tier.id} />
          </Link>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={enabled}
            // An inactive tier can be taken out of the waterfall, not put in.
            disabled={!enabled && tier.objectState !== 'ACTIVE'}
            onCheckedChange={(checked) => toggle(tier.id, checked)}
            aria-label={t('tiers.enableTier', { name: tier.name })}
          />
        </TableCell>
        <TableCell className="text-(--ad-text-muted)">{t(`tiers.types.${tier.type}`)}</TableCell>
        <TableCell>{formatNumber(tier.minimumTraffic)}%</TableCell>
        <TableCell>{formatNumber(tier.maximumTraffic)}%</TableCell>
        <TableCell className="whitespace-normal text-(--ad-text-muted)">
          {tier.description || '—'}
        </TableCell>
        <TableCell>
          <StatusChip state={tier.objectState} />
        </TableCell>
      </>
    )
  }

  const rowProps = (id: string) => ({
    className: 'cursor-pointer',
    onClick: () => navigate(`/admin/tiers/${id}`),
  })

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-0" />
          <TableHead className="w-0">{t('tiers.priority')}</TableHead>
          <TableHead>{t('common.name')}</TableHead>
          <TableHead>{t('tiers.enabled')}</TableHead>
          <TableHead>{t('common.type')}</TableHead>
          <TableHead>{t('tiers.minimumTraffic')}</TableHead>
          <TableHead>{t('tiers.maximumTraffic')}</TableHead>
          <TableHead>{t('common.description')}</TableHead>
          <TableHead>{t('common.status')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          Array.from({ length: 3 }, (_, i) => (
            <TableRow key={i}>
              <TableCell colSpan={columnCount}>
                <Skeleton className="h-5 w-full" />
              </TableCell>
            </TableRow>
          ))
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="py-8 text-center text-(--ad-text-muted)">
              {t('tiers.noTiersFound')}
            </TableCell>
          </TableRow>
        ) : (
          <>
            <SortableTableRows ids={enabledIds} onReorder={onChange}>
              {enabledRows.map((tier) => (
                <SortableTableRow
                  key={tier.id}
                  id={tier.id}
                  handleLabel={t('tiers.dragToReorder', { name: tier.name })}
                  {...rowProps(tier.id)}
                >
                  {cells(tier)}
                </SortableTableRow>
              ))}
            </SortableTableRows>
            {disabledRows.length > 0 && (
              <>
                <TableRow className="hover:bg-transparent">
                  <TableHead colSpan={columnCount}>{t('tiers.disabledTiers')}</TableHead>
                </TableRow>
                {disabledRows.map((tier) => (
                  <TableRow key={tier.id} {...rowProps(tier.id)}>
                    <TableCell className="w-0 pr-0" />
                    {cells(tier)}
                  </TableRow>
                ))}
              </>
            )}
          </>
        )}
      </TableBody>
    </Table>
  )
}

/** Whether the network has the tier in its waterfall. */
export function TierEnabledChip({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation()
  return (
    <span className={`chip chip--${enabled ? 'running' : 'draft'} text-[11px]`}>
      <span className="dot" />
      {enabled ? t('tiers.enabled') : t('tiers.disabled')}
    </span>
  )
}
